import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChartTheme } from "@/hooks/useChartTheme";
import { PageHeader } from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { StatusBadge } from "@/components/StatusBadge";
import { SourceBadge } from "@/components/SourceBadge";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { STATUS } from "@/lib/constants";
import { formatDate, formatKr, formatMonth, daysLabel, copyText } from "@/lib/format";
import { toast } from "sonner";
import { Upload, Mail, Copy, ArrowRight, CalendarClock, AlertTriangle, Inbox } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const KPI_ORDER = ["til_betaling", "forfaller_snart", "forfalt", "ma_kontrolleres", "mulig_duplikat", "avtalegiro"];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border bg-card p-3 text-sm shadow-md">
      <p className="font-medium">{formatMonth(label)}</p>
      <p className="tnum">{d.count} regninger · {formatKr(d.total)}</p>
      <p className="text-xs text-muted-foreground tnum">Ubetalt {formatKr(d.unpaid)} · Betalt {formatKr(d.paid)}{d.avtalegiro ? ` · AvtaleGiro ${formatKr(d.avtalegiro)}` : ""}</p>
    </div>
  );
};

const InvoiceMini = ({ inv, showDays }) => (
  <Link to={`/fakturaer/${inv.id}`} className="flex items-center justify-between gap-3 py-3 border-b last:border-b-0 hover:bg-secondary/60 -mx-2 px-2 rounded-md transition-colors" data-testid={`dashboard-invoice-${inv.id}`}>
    <div className="min-w-0">
      <p className="font-medium truncate">{inv.issuer_name || "Ukjent avsender"}</p>
      <p className="text-xs text-muted-foreground">{showDays && inv.days_to_due !== null ? daysLabel(inv.days_to_due) : formatDate(inv.due_date)} · <SourceBadge source={inv.source} className="bg-transparent px-0 py-0" /></p>
    </div>
    <div className="text-right shrink-0">
      <p className="font-medium tnum">{formatKr(inv.amount, inv.currency)}</p>
      <StatusBadge status={inv.display_status || inv.status} />
    </div>
  </Link>
);

export default function DashboardPage() {
  const { user, actAs, canWrite } = useAuth();
  const navigate = useNavigate();
  const chart = useChartTheme();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch((e) => setError(errorMessage(e)));
  }, []);

  const firstName = (user?.full_name || "").split(" ")[0];
  const ownerFirst = (actAs?.owner_name || "").split(" ")[0];

  if (error) return <EmptyState icon={AlertTriangle} title="Kunne ikke laste oversikten" description={error} actionLabel="Prøv igjen" onAction={() => window.location.reload()} />;

  if (!data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4">{KPI_ORDER.map((k) => <Skeleton key={k} className="h-28" />)}</div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  const totalInvoices = Object.values(data.kpis).reduce((a, b) => a + b.count, 0) + (data.recent?.length || 0);
  const isEmpty = totalInvoices === 0 && data.recent.length === 0;

  return (
    <div className="space-y-6 md:space-y-8">
      <PageHeader
        eyebrow={new Date().toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long" })}
        title={actAs ? `Regningene til ${ownerFirst || actAs.owner_name}` : `Hei${firstName ? `, ${firstName}` : ""}`}
        description={data.kpis.til_betaling.count ? `${actAs ? `${ownerFirst || "Kontoen"} har` : "Du har"} ${data.kpis.til_betaling.count} ${data.kpis.til_betaling.count === 1 ? "regning" : "regninger"} til betaling på totalt ${formatKr(data.kpis.til_betaling.sum)}.` : "Ingen ubetalte regninger akkurat nå."}
        actions={canWrite ? (
          <>
            <Button variant="ghost" asChild className="hidden sm:inline-flex" data-testid="dashboard-manual-button"><Link to="/registrer">Registrer manuelt</Link></Button>
            <Button asChild data-testid="dashboard-upload-button"><Link to="/last-opp"><Upload className="h-4 w-4 mr-2" /> Last opp regning</Link></Button>
          </>
        ) : null}
      />

      {isEmpty ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {canWrite ? (
            <EmptyState icon={Inbox} title="Velkommen til Controlle" description="Start med å laste opp en PDF eller ta bilde av en regning. Vi leser leverandør, beløp, KID og forfallsdato automatisk." actionLabel="Last opp din første regning" actionTo="/last-opp" testId="dashboard-empty-state" />
          ) : (
            <EmptyState icon={Inbox} title="Ingen regninger ennå" description={`${actAs?.owner_name || "Kontoeieren"} har ikke registrert noen regninger enda.`} testId="dashboard-empty-state" />
          )}
          {!actAs && user?.forwarding_address ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base"><Mail className="h-4 w-4 text-primary" /> Din personlige e-postadresse</CardTitle>
                <CardDescription>Videresend fakturaer du får på e-post hit, så havner de automatisk i oversikten.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 rounded-lg border bg-secondary px-3 py-2">
                  <code className="text-sm flex-1 truncate" data-testid="dashboard-forwarding-address">{user?.forwarding_address}</code>
                  <Button size="sm" variant="outline" onClick={async () => { await copyText(user?.forwarding_address); toast.success("Adresse kopiert"); }} data-testid="dashboard-copy-forwarding"><Copy className="h-4 w-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </div>
      ) : null}

      <section aria-label="Nøkkeltall" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4">
        {KPI_ORDER.map((k, i) => (
          <KpiCard key={k} statusKey={k} label={STATUS[k].label} icon={STATUS[k].icon} count={data.kpis[k].count} sum={data.kpis[k].sum} to={`/fakturaer?status=${k}`} index={i} />
        ))}
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-4 md:gap-6">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between space-y-0">
            <div>
              <CardTitle className="text-base">Månedsoversikt</CardTitle>
              <CardDescription>Regninger etter forfallsmåned – klikk på en måned for detaljer.</CardDescription>
            </div>
            <Button variant="ghost" size="sm" asChild><Link to="/maanedsoversikt" data-testid="dashboard-monthly-link">Se alle <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
          </CardHeader>
          <CardContent>
            <div className="h-64" data-testid="dashboard-monthly-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.monthly} margin={{ top: 8, right: 8, left: -12, bottom: 0 }} onClick={(s) => { if (s?.activeLabel) navigate(`/fakturaer?date_from=${s.activeLabel}-01&date_to=${s.activeLabel}-31`); }}>
                  <CartesianGrid stroke={chart.grid} vertical={false} />
                  <XAxis dataKey="month" tickFormatter={(m) => formatMonth(m, true)} tick={{ fill: chart.tick, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: chart.tick, fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                  <RTooltip content={<ChartTooltip />} cursor={{ fill: chart.cursor }} />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]} maxBarSize={40} className="cursor-pointer">
                    {data.monthly.map((m) => <Cell key={m.month} fill={m.is_current ? chart.primary : chart.primarySoft} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> Forfaller de neste 7 dagene</CardTitle>
            <CardDescription>{data.upcoming.length ? `${data.upcoming.length} regninger` : "Ingen regninger forfaller den neste uken."}</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {data.upcoming.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Alt under kontroll.</p> : data.upcoming.map((inv) => <InvoiceMini key={inv.id} inv={inv} showDays />)}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between space-y-0">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-warn" /> Trenger din oppmerksomhet</CardTitle>
              <CardDescription>Fakturaer som må kontrolleres eller kan være duplikater.</CardDescription>
            </div>
            <Button variant="ghost" size="sm" asChild><Link to="/fakturaer?status=ma_kontrolleres">Se alle <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
          </CardHeader>
          <CardContent className="pt-0">
            {data.attention.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Ingenting å kontrollere.</p> : data.attention.map((inv) => <InvoiceMini key={inv.id} inv={inv} />)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-start justify-between space-y-0">
            <div>
              <CardTitle className="text-base">Nylig mottatt</CardTitle>
              <CardDescription>De siste regningene som kom inn.</CardDescription>
            </div>
            <Button variant="ghost" size="sm" asChild><Link to="/fakturaer">Alle fakturaer <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
          </CardHeader>
          <CardContent className="pt-0">
            {data.recent.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Ingen regninger ennå.</p> : data.recent.map((inv) => <InvoiceMini key={inv.id} inv={inv} />)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
